import Vue from 'vue'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        Component = await Component()
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,

      payload: context.payload,
      error: context.error,
      base: '/static/signup/',
      env: {"VUE_APP_IRONCLAD_SITE_ACCESS_ID":"3652e4b9-a327-430c-8ceb-c8f68a2bbd24","VUE_APP_IRONCLAD_CLICKWRAP_GROUP_KEY_BRAND_AFFILIATE":"embedded-brand-affiliate","VUE_APP_IRONCLAD_CLICKWRAP_GROUP_KEY_MEMBER":"embedded-ns-member","VUE_APP_IRONCLAD_CLICKWRAP_GROUP_KEY_CUSTOMER":"embedded-ns-customer","VUE_APP_EMAIL_DUPLICATION_ENDPOINT":"api.cloud.nuskin.com/email-tools/v1","FF_USE_WINDOWS_LEGACY_PROCESS_STRATEGY":"false","FF_SCRIPT_SECTIONS":"false","FF_POSIXLY_CORRECT_ESCAPES":"false","HARBOR_USERNAME":"robot-gitlab-operator","CI_JOB_MANUAL":"true","CI_SERVER_VERSION_PATCH":"5","CI_SERVER_REVISION":"d7f0517433c","CI_COMMIT_SHORT_SHA":"0773a0e7","CI_COMMIT_TAG_MESSAGE":"","SAST_EXCLUDED_ANALYZERS":"eslint","S3_BUCKET_TEST":"nse-test-nuskin-com-static","GITLAB_USER_LOGIN":"jporter","npm_package_resolutions_firebase":"9.9.x","npm_package_devDependencies_identity_obj_proxy":"^3.0.0","npm_package_dependencies__nuskin_ns_util":"4.4.0","npm_package_scripts_dev_mobile":"cross-env APP_ENV=local NUXT_HOST=0.0.0.0 NUXT_PORT=3001 nuxt","HARBOR_OCI":"oci://registry.shd.nuskin.io","CI_DEPENDENCY_PROXY_PASSWORD":"glcbt-64_2rnmNS42y-PyvQxLde1p","CI_DEPENDENCY_PROXY_SERVER":"code.tls.nuskin.io:443","CX_BASE_AUTH_URI":"https://iam.checkmarx.net","npm_package_devDependencies_babel_core":"7.0.0-bridge.0","FF_USE_DUMB_INIT_WITH_KUBERNETES_EXECUTOR":"false","SEM_RELEASE_NPM_MODULE":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/SEM_RELEASE_NPM_MODULE","npm_package_resolutions_npm_user_validate":"1.0.1","npm_package_resolutions_minimist":"1.2.6","npm_package_devDependencies_jest_transform_styl":"^1.0.0","npm_package_devDependencies_jest":"^24.0.0","npm_package_devDependencies_eslint_plugin_security":"^1.4.0","npm_package_devDependencies__testing_library_jest_dom":"5.0.2","npm_package_devDependencies__nuxtjs_proxy":"^2.0.1","npm_package_devDependencies__nuxt_types":"^2.14.1","npm_package_dependencies__nuskin_account_components":"1.139.0","npm_config_version_commit_hooks":"true","npm_config_user_agent":"yarn/1.22.19 npm/? node/v16.18.1 linux x64","FF_USE_LEGACY_KUBERNETES_EXECUTION_STRATEGY":"false","FF_LOG_IMAGES_CONFIGURED_FOR_JOB":"false","FF_USE_LEGACY_S3_CACHE_ADAPTER":"false","CI":"true","CI_SERVER_PROTOCOL":"https","CI_PROJECT_NAME":"signup","NODE_IMAGE":"code.tls.nuskin.io:5005/custom-templates/custom-images:node16.18.1","DATADOG_SITE":"datadoghq.com","SEMP_BASIC_AUTH_PRODUCTION":"cHJvZHVjdGlvbi1hZG1pbjpmbmliZm1pZzBoZjZncW9jYTBvM2w0OWQxYQ==","SIGNUP_API_CLIENT_ID":"735b1eb810304bba966af0891ab54053","CI_RUNNER_REVISION":"ef334dcc","npm_package_devDependencies__testing_library_user_event":"^13.1.9","npm_package_dependencies_vue":"^2.6.11","npm_package_dependencies_object_path":"0.11.8","npm_package_dependencies__nuskin_app_components":"3.22.1","npm_config_bin_links":"true","HOSTNAME":"runner-t1b56g--project-1040-concurrent-0","CI_JOB_STAGE":"deploy-to-prod","CI_PROJECT_DESCRIPTION":"","CI_COMMIT_DESCRIPTION":"\nUpdate: [NsAddress] updated api off of kong #PA-39\n\nCloses PA-39\n\nSee merge request ns-am/accounts/app/signup!583","DD_LAYER_VER":"98","SEMP_BASIC_AUTH_TESTING":"dGVzdGluZy1hZG1pbjpoNWZkb2cybWhqanM1MmIwZjVzZGdpMDVzcw==","TEST_RAIL_KEY":"d843rftfwcgMxOaPH6wv-VPDEmD.SHAmqBzvSlqXb","SIGNUP_API_CLIENT_SECRET":"0deea37049e84157A406C0C01D29F300","npm_node_execpath":"/usr/local/bin/node","npm_package_devDependencies_vue_jest":"4.0.0-beta.5","npm_package_dependencies__okta_okta_auth_js":"5.4.2","npm_package_dependencies__nuskin_design_components":"8.8.1","npm_config_init_version":"1.0.0","HARBOR_PROJECT":"nuskin","CI_DEPENDENCY_PROXY_USER":"gitlab-ci-token","CI_SERVER_VERSION":"17.8.5-ee","SAST_IMAGE_SUFFIX":"","DOCKER_AUTH_CONFIG":"{\n    \"auths\": {\n        \"registry.shd.nuskin.io\": {\n            \"auth\": \"cm9ib3QtZ2l0bGFiLW9wZXJhdG9yOkwyUlNlVzYzWTBZRHFLRG1wbDQ3QVpCY3VKMk9iSFky\"\n        }\n    }\n}","SEM_RELEASE_FLUTTER_MODULE":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/SEM_RELEASE_FLUTTER_MODULE","DOCKER_REGISTRY_DEPLOY_USERNAME":"docker-registry-deploy-token","SHLVL":"5","npm_package_resolutions_shell_quote":"1.7.3","npm_package_dependencies_html_webpack_plugin":"^4.3.0","FF_DISABLE_POWERSHELL_STDIN":"false","FF_CLEAN_UP_FAILED_CACHE_EXTRACT":"false","FF_DISABLE_AUTOMATIC_TOKEN_ROTATION":"false","CI_PROJECT_ROOT_NAMESPACE":"ns-am","SPLUNK_ACCESS_TOKEN":"kIy5jbdT_ZYSsqIMecolhw","DATADOG_API_KEY":"c1477540c777fb7506439ac698ea9233","HOME":"/root","OLDPWD":"/","npm_package_resolutions_ssri":"6.0.2","npm_package_devDependencies_eslint_plugin_babel":"^5.3.0","FF_NETWORK_PER_BUILD":"false","CI_JOB_ID":"2183757","CI_SERVER_HOST":"code.tls.nuskin.io","CI_COMMIT_REF_NAME":"v1.96.0","SECRET_DETECTION_EXCLUDED_PATHS":"coverage","SPLUNK_REALM":"us1","npm_package_resolutions__nuskin_ns_util":"4.x.x","npm_package_devDependencies_eslint_config_prettier":"^4.1.0","FF_RESOLVE_FULL_TLS_CHAIN":"false","CI_PIPELINE_SOURCE":"push","DS_SCHEMA_MODEL":"15","SNS_OPS_TOPIC":"nse-pri-tls-us-west-2-devops-sns","SEM_RELEASE_OPTIONS":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/SEM_RELEASE_OPTIONS","GL_TOKEN":"glpat-JTuPnMmUz6WRxXTt5w_x","CI_RUNNER_VERSION":"17.10.1","npm_package_resolutions_ip":"2.0.0","npm_package_dependencies_vue_mq":"^1.0.1","npm_package_dependencies_vue_headful":"^2.1.0","npm_package_dependencies_google_auth_library":"6","npm_package_dependencies__nuskin_ns_loyalty_web":"1.5.2","npm_package_scripts_dev_local":"cross-env APP_ENV=local APP_COMP_ENV=local MOCK_API=false nuxt --port 3001","npm_config_init_license":"MIT","FF_SKIP_NOOP_BUILD_STAGES":"true","FF_USE_FASTZIP":"false","FF_USE_WINDOWS_JOB_OBJECT":"false","CI_BUILDS_DIR":"/gitlab-runner-data/builds","CI_ENVIRONMENT_NAME":"prod","CI_SERVER_VERSION_MAJOR":"17","CI_DEFAULT_BRANCH":"master","CONTENT_TYPE_UID":"application","SONAR_HOST_URL":"https://sonar.nuskin.io","YARN_WRAP_OUTPUT":"false","npm_package_resolutions_ansi_html":"0.0.8","npm_package_devDependencies__nuxtjs_vuetify":"^0.5.0","npm_package_dependencies__nuskin_ns_core_styles":"2.12.0","npm_package_scripts_local_selenium":"echo \"Make sure 'yarn start' is running!\" && cd integration/ui && mvn test-compile surefire:test -Dhost=local -Denvironment=local","npm_config_version_tag_prefix":"v","CI_REGISTRY_PASSWORD":"glcbt-64_2rnmNS42y-PyvQxLde1p","CI_ENVIRONMENT_ACTION":"start","CI_SERVER_URL":"https://code.tls.nuskin.io","CI_TEMPLATE_REGISTRY_HOST":"registry.gitlab.com","CI_COMMIT_REF_PROTECTED":"false","npm_package_devDependencies_jest_extended":"^0.11.5","GITLAB_FEATURES":"audit_events,blocked_issues,blocked_work_items,board_iteration_lists,code_owners,code_review_analytics,full_codequality_report,group_activity_analytics,group_bulk_edit,issuable_default_templates,issue_weights,iterations,ldap_group_sync,merge_request_approvers,milestone_charts,multiple_issue_assignees,multiple_ldap_servers,multiple_merge_request_assignees,multiple_merge_request_reviewers,project_merge_request_analytics,protected_refs_for_users,push_rules,resource_access_token,seat_link,seat_usage_quotas,pipelines_usage_quotas,transfer_usage_quotas,product_analytics_usage_quotas,wip_limits,zoekt_code_search,description_diffs,send_emails_from_admin_area,repository_size_limit,maintenance_mode,scoped_issue_board,contribution_analytics,group_webhooks,member_lock,elastic_search,repository_mirrors,ai_chat,adjourned_deletion_for_projects_and_groups,admin_audit_log,auditor_user,blocking_merge_requests,board_assignee_lists,board_milestone_lists,ci_secrets_management,ci_pipeline_cancellation_restrictions,cluster_agents_ci_impersonation,cluster_agents_user_impersonation,cluster_deployments,code_owner_approval_required,code_suggestions,commit_committer_check,commit_committer_name_check,compliance_framework,custom_compliance_frameworks,custom_fields,custom_file_templates,custom_project_templates,cycle_analytics_for_groups,cycle_analytics_for_projects,db_load_balancing,default_branch_protection_restriction_in_groups,default_project_deletion_protection,delete_unconfirmed_users,dependency_proxy_for_packages,disable_extensions_marketplace_for_enterprise_users,disable_name_update_for_users,disable_personal_access_tokens,domain_verification,epic_colors,epics,extended_audit_events,external_authorization_service_api_management,feature_flags_code_references,file_locks,geo,generic_alert_fingerprinting,git_two_factor_enforcement,group_allowed_email_domains,group_coverage_reports,group_forking_protection,group_level_compliance_dashboard,group_milestone_project_releases,group_project_templates,group_repository_analytics,group_saml,group_scoped_ci_variables,ide_schema_config,incident_metric_upload,instance_level_scim,jira_issues_integration,ldap_group_sync_filter,linked_items_epics,merge_request_performance_metrics,admin_merge_request_approvers_rules,merge_trains,metrics_reports,multiple_alert_http_integrations,multiple_approval_rules,multiple_group_issue_boards,object_storage,microsoft_group_sync,operations_dashboard,package_forwarding,pages_size_limit,pages_multiple_versions,productivity_analytics,project_aliases,protected_environments,reject_non_dco_commits,reject_unsigned_commits,related_epics,remote_development,saml_group_sync,service_accounts,scoped_labels,smartcard_auth,ssh_certificates,swimlanes,target_branch_rules,type_of_work_analytics,minimal_access_role,unprotection_restrictions,ci_project_subscriptions,incident_timeline_view,oncall_schedules,escalation_policies,zentao_issues_integration,coverage_check_approval_rule,issuable_resource_links,group_protected_branches,group_level_merge_checks_setting,oidc_client_groups_claim,disable_deleting_account_for_users,disable_private_profiles,group_saved_replies,requested_changes_block_merge_request,project_saved_replies,default_roles_assignees,ci_component_usages_in_projects,branch_rule_squash_options,group_ip_restriction,issues_analytics,password_complexity,group_wikis,email_additional_text,custom_file_templates_for_namespace,incident_sla,export_user_permissions,cross_project_pipelines,feature_flags_related_issues,merge_pipelines,ci_cd_projects,github_integration,ai_agents,ai_config_chat,ai_features,ai_review_mr,ai_workflows,amazon_q,api_discovery,api_fuzzing,auto_rollback,glab_ask_git_command,cluster_receptive_agents,cluster_image_scanning,external_status_checks,combined_project_analytics_dashboards,compliance_pipeline_configuration,container_scanning,credentials_inventory,custom_roles,dast,dependency_scanning,dora4_analytics,enterprise_templates,environment_alerts,evaluate_group_level_compliance_pipeline,explain_code,external_audit_events,experimental_features,generate_description,generate_commit_message,generate_test_file,ai_generate_cube_query,git_abuse_rate_limit,group_ci_cd_analytics,group_level_compliance_adherence_report,group_level_compliance_violations_report,project_level_compliance_dashboard,project_level_compliance_adherence_report,project_level_compliance_violations_report,group_level_analytics_dashboard,incident_management,inline_codequality,insights,integrations_allow_list,issuable_health_status,issues_completed_analytics,jira_vulnerabilities_integration,jira_issue_association_enforcement,kubernetes_cluster_vulnerabilities,license_scanning,okrs,personal_access_token_expiration_policy,pre_receive_secret_detection,product_analytics,project_quality_summary,project_level_analytics_dashboard,quality_management,release_evidence_test_artifacts,report_approver_rules,required_ci_templates,requirements,runner_maintenance_note,runner_performance_insights,runner_performance_insights_for_namespace,runner_upgrade_management,runner_upgrade_management_for_namespace,sast,sast_advanced,sast_iac,sast_custom_rulesets,sast_fp_reduction,secret_detection,security_configuration_in_ui,security_dashboard,security_on_demand_scans,security_orchestration_policies,security_training,ssh_key_expiration_policy,summarize_mr_changes,summarize_my_mr_code_review,summarize_new_merge_request,summarize_comments,stale_runner_cleanup_for_namespace,status_page,suggested_reviewers,subepics,observability,troubleshoot_job,unique_project_download_limit,vulnerability_finding_signatures,container_scanning_for_registry,security_exclusions,security_scans_api,observability_alerts,measure_comment_temperature,coverage_fuzzing,devops_adoption,group_level_devops_adoption,instance_level_devops_adoption","CI_PROJECT_ID":"1040","CI_REGISTRY_IMAGE":"code.tls.nuskin.io:5005/ns-am/accounts/app/signup","APP_NAME":"signup","npm_package_dependencies__nuskin_ns_product":"^3.35.2","npm_package_dependencies__nuskin_axios_util":"2.3.0","npm_package_scripts_test_updateSnapshots":"jest --updateSnapshot","FF_USE_LEGACY_GCS_CACHE_ADAPTER":"false","GITLAB_CI":"true","CI_COMMIT_SHA":"0773a0e77902b5b32187a8ef504854d0114ecb55","CI_COMMIT_TAG":"v1.96.0","SECURE_ANALYZERS_PREFIX":"registry.gitlab.com/security-products","SCHEDULED_TARGET_ENVIRONMENT":"dev","npm_package_devDependencies_typescript":"3","npm_package_devDependencies_babel_loader":"^8.2.3","npm_package_scripts_test_selenium":"yarn build-dev && start-server-and-test start 3001 local-selenium","npm_package_description":"Signup App","FF_RETRIEVE_POD_WARNING_EVENTS":"true","CI_CONCURRENT_ID":"0","SECRET_DETECTION_IMAGE_SUFFIX":"","NPM_TOKEN":"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","npm_package_readmeFilename":"README.md","npm_package_devDependencies__babel_preset_env":"7.11.0","npm_package_dependencies_nuxt":"^2.14.1","npm_package_scripts_build_dev":"cp ./env/dev.env ./.env && nuxt generate --fail-on-page-error","FF_DISABLE_UMASK_FOR_DOCKER_EXECUTOR":"false","FF_USE_DOCKER_AUTOSCALER_DIAL_STDIO":"true","CI_REGISTRY_USER":"gitlab-ci-token","CI_SERVER_PORT":"443","npm_package_devDependencies_prettier":"^1.16.4","npm_package_devDependencies_jest_silent_reporter":"^0.2.1","npm_package_devDependencies_babel_jest":"^24.0.0","npm_package_devDependencies__babel_plugin_transform_runtime":"^7.11.0","npm_package_dependencies__nuskin_invite_link_service":"2.3.5","npm_package_scripts_build_prod":"cp ./env/prod.env ./.env && nuxt generate --fail-on-page-error","npm_package_scripts_dev":"cross-env APP_ENV=local APP_COMP_ENV=test MOCK_API=false nuxt --port 3001","CI_PROJECT_DIR":"/gitlab-runner-data/builds/ns-am/accounts/app/signup","CI_PROJECT_PATH":"ns-am/accounts/app/signup","S3_BUCKET_FEATURE_DEV":"apps.dev.nuskin.io","npm_package_devDependencies_vue_template_compiler":"^2.6.11","npm_package_devDependencies__testing_library_dom":"^7.31.2","npm_package_dependencies_lodash_get":"^4.4.2","FF_ENABLE_JOB_CLEANUP":"false","FF_WAIT_FOR_POD_TO_BE_REACHABLE":"false","CI_PROJECT_NAMESPACE":"ns-am/accounts/app","CI_COMMIT_TIMESTAMP":"2025-03-26T19:56:03+00:00","DS_MAJOR_VERSION":"5","SCAN_KUBERNETES_MANIFESTS":"false","SOLACE_BASIC_AUTH_PRODUCTION":"cHJvZHVjdGlvbi1hZG1pbjpmbmliZm1pZzBoZjZncW9jYTBvM2w0OWQxYQ==","npm_package_resolutions_ua_parser_js":"0.7.24","npm_package_resolutions_handlebars":"4.7.7","npm_package_dependencies_lodash_flatten":"^4.4.0","npm_package_scripts_before_mr":"yarn lint && yarn test && yarn test:selenium","npm_package_scripts_test_debug":"node --inspect ./node_modules/.bin/jest --runInBand --watch","npm_package_private":"true","FF_USE_DIRECT_DOWNLOAD":"true","FF_USE_DYNAMIC_TRACE_FORCE_SEND_INTERVAL":"false","CI_JOB_TOKEN":"glcbt-64_2rnmNS42y-PyvQxLde1p","CI_NODE_TOTAL":"1","CI_SERVER_NAME":"GitLab","DEV_PORTAL_S3_BUCKET_BASE":"nuskin-ENV-portalcontent-us-west-2-standard","SOLACE_BASIC_AUTH_TESTING":"dGVzdGluZy1hZG1pbjpoNWZkb2cybWhqanM1MmIwZjVzZGdpMDVzcw==","npm_package_resolutions_json_schema":"0.4.0","npm_package_devDependencies_vuetify_loader":"^1.6.0","npm_package_scripts_lint":"eslint --ext .js,.vue components config layouts pages plugins test utils","npm_config_registry":"https://registry.yarnpkg.com","CI_PROJECT_NAMESPACE_ID":"466","CI_PIPELINE_CREATED_AT":"2025-04-04T17:51:15Z","DEV_PORTAL_TOOLS_S3_BUCKET":"nuskin-tls-portalcontent-us-west-2-standard","CX_APIKEY":"eyJhbGciOiJIUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICIyM2JmODVjMi02NDZmLTQwMzMtOTE1Yy03YTQ4NDJmMjIwM2UifQ.eyJpYXQiOjE2NjgxMDgzMzEsImp0aSI6ImI0NDA3YjA0LWY1MTctNGE0Yy1iMzI1LWI5NGI2NGVjZGE5NCIsImlzcyI6Imh0dHBzOi8vaWFtLmNoZWNrbWFyeC5uZXQvYXV0aC9yZWFsbXMvbnVza2luIiwiYXVkIjoiaHR0cHM6Ly9pYW0uY2hlY2ttYXJ4Lm5ldC9hdXRoL3JlYWxtcy9udXNraW4iLCJzdWIiOiIyZGM3MTJjOS1lNDZhLTQ5N2EtYTI5ZS0yMDNhMWViZTllYzkiLCJ0eXAiOiJPZmZsaW5lIiwiYXpwIjoiYXN0LWFwcCIsInNlc3Npb25fc3RhdGUiOiJjNWFkNWFmMS02NmI3LTRmNmUtYWFlOS1kYjFmNWY4ZTNhYTciLCJzY29wZSI6IiBvZmZsaW5lX2FjY2VzcyIsInNpZCI6ImM1YWQ1YWYxLTY2YjctNGY2ZS1hYWU5LWRiMWY1ZjhlM2FhNyJ9.gnPSkrjU1K3b6sLhQn2kwsiksqIRBJShlZFb9Vn64DU","CODEARTIFACT_AUTH_TOKEN":"eyJ2ZXIiOjEsImlzdSI6MTc0NDIwMDE5NywiZW5jIjoiQTEyOEdDTSIsInRhZyI6Ims0VHpZdVVZWEhNbTlKdVJoZlVaRkEiLCJleHAiOjE3NDQyNDMzOTcsImFsZyI6IkExMjhHQ01LVyIsIml2Ijoib3lNTW5zVzNaRURGZF83aiJ9.pyvQdD_xSCDBOmM6HlL96A.LGuWjVZJPTlgMc-G.aR5QYsR5QP01O40DYlLZ19bqM6YQBVWQuoVlXYn6eYbB5XrIAQ0TnM-q1qIrwBQ-a4Ll8fhmKvydNpUgbRdTELBsqGLuX27EaoaBbd6PqCjtvfr8Gtvkiuxj54Fp9n8SWHujoxDctRuxLFJwyPG-qtwdwXCD2wOv3wfusioCHNfoyFmOT1znlWCxCTuMTHIuHbux2r9vhftWMn1vEaqzzHVloHlH5ZTOOoHWfIX4ySAbPhHyjYZKrtMm1VM_XMRCWCSeP5aKvcop5egc3aRBcVnKTUpyMUt68rjFpigemd0wcPZfuOXeMSwjcsArZKWQoh_31Xz7YbElYN6PBR9HGpDY9CklCpmO2QDs8TgkeKI4reD0WKd7M7EAG-lhEeLbx47W2hO1tJuGqHtM2vI_2O8cNP-tStdiYr7VOQODYD9gSBokES3ckpfzmqBSzNBH30ZNIk8dkv1pO-RNfxf9jDqxS6i-NxORL933_X--V22bFFrR3sXmRfCd0pAqMkTBhW_NHNOZlbPtsVakNSCtam_Fbf1Dh1lcpLG5PQxow4mXzsGC12AvVhLlpHvZ_QmS2r7jM3x6vX4CUQ4JhA-Bg3thytYPPKgi372mwvrc4kHyodrmAYD_VDloanJoUNO4mkOAMk7UnHXzE-ZgSRroXmaQDL16LL5SKXw0x4ahhnyUXb-a6NsZt8igN41RXSBEcUBEVrKkBKAC4Zi1bUEkK6UsHFUAMtb-cewlQzxTgJGwv7vKwGnMKvD5Y416tkpaFQg0RXfiK-fAmhSaS9TUJHwjsP8GoNbrmaoHExwqP4NFE9p0bz3cAN6am4ogOjOn21TAE6U7yzzTpisiZpKnlZA9Y2uAjJpgySPVy9YE4ty5uJLU3VhFT4m7zj3LdOyVGAPwTnE4SaxQ0Cu8oYjf_aI8BuXNDDsUQDhRWqk1DPNZyw202Bcp6PceWCaFki6_ozJ5XAUwMQ5Yd9iGuNNVrTZj4psE6wu4eIJMqOB_I3PJBoNBku-i3GffL7FD1nLwHtQ78Xcv4zJD8vsyBw80u-4LApXrf4ZCgZfT4L_FnyVTSuanoJhkCtJ4GlwrP-ib4OX4vsbXljjIpThOuUBCrHNYIgMoY6WK9jcrVPGQu_fsSfHBDeAKewH7pFUCy4bJyAvTcK7Kv4czxJ1A2G38Y-9-bcsUsOeskAc-SZ3C9IS1rG3-sUJuEZNahj5Xd8x5VaGwIxs4j-buG1XpeNLfXHqgyOKazcUBjBkcf2OZNQR6EU1PUzsj4EJqzW4zHbp8crZNzCSJ_RenurQcxGU2Z0pd1yMFH_Eu6yHB73QBc-CDv49oqVVAnzGiloA_JfGg-YnrPfyRePzfnvdQKt-GlxXOkrKJYjZnTfc4wxSKpB9KrPjxyGhwpTxSa5gWecjyb2U9hs5EhnYwKE7MnV5KRmXfd4vvuUGMIutxK52EYwjyNxEvSoafC7rCnDplCFmHtOkQjPV-t5sXwr5xW8q_cvrVDsGcxvtPTRqUgMdFdZ7VqKC6-_TotJ0bij-Vv66u6nCNWkOSpXd-KnHaEbG2CzXSedTfER4RIw3r_K0eqD0eET9aFYMnWCSXG5EVdu_oBmx0aY8OufeaU5hzk2yLzDbZw65EY26ZMJZqZVDXaxnyE-BQLtWqwKvVybEkxAYcoAwayuMcMgSkUqmPUyHBKozcI8MevUy5Jg2_Kafh4GGHDiLIXUIHf3eNh2P_i6-20VN0qn4VDG8t8nvWVD78nKXIOl7E8cv8P7zP4DWUJdXMq8bKdtRX_VHPuO8n9HVXCi5_EZedSicf0dFG1LnIVC67-mhpZnb5Pe5ATfBKBIZQ_m4A6UmZMI2YnxW4UbL17lRLUr4wVjn6Pr288PA.xxzURPSl_73dDp0vm1DZBw","npm_package_resolutions_moment":"2.29.2","npm_package_resolutions_tough_cookie":"4.1.3","npm_package_devDependencies_axios_cache_adapter":"2.7.3","npm_package_devDependencies__babel_core":"^7.11.1","npm_package_dependencies_vuetify":"1","npm_package_dependencies_core_js":"^2.6.11","npm_package_scripts_lintall":"echo \"lintall is deprecated, use lint instead\" && yarn lint","CI_CONCURRENT_PROJECT_ID":"0","HARBOR_PASSWORD":"L2RSeW63Y0YDqKDmpl47AZBcuJ2ObHY2","CI_JOB_NAME_SLUG":"deploy-to-prod","SOLACE_API_TOKEN_DEVELOPMENT":"eyJhbGciOiJSUzI1NiIsImtpZCI6Im1hYXNfcHJvZF8yMDIwMDMyNiIsInR5cCI6IkpXVCJ9.eyJvcmciOiJudXNraW4iLCJvcmdUeXBlIjoiRU5URVJQUklTRSIsInN1YiI6InZpY3g0aDdkbDc5IiwicGVybWlzc2lvbnMiOiJBQUFBQUlBUEFBQUFmemdBNEFFQUFBQUFBQUFBQUFBQUFJQ3h6b2NoSUFqZ0wvLy9nNWZmQmQ0RFdNTUQ0NGU1TVlNWkVQNS9CZ0FJIiwiYXBpVG9rZW5JZCI6ImVzdWFpanJwcGZ1IiwiaXNzIjoiU29sYWNlIENvcnBvcmF0aW9uIiwiaWF0IjoxNzIxMDg3MDI3fQ.CO2VHDgHhuId7_v1JfL01mX7hyFHxlMWV1iZKuSIkjAV4LfgSbX4rMrormqADzbwgyDVAGsaqzfeh2-G6_sv7ereyCGQXRztlD_p41JKaZ-IMApc_ZcxJQYHku7pT_Tnyt7HWqiSU4InT8K-R7AuJfCoGkd_Y-HZHItulQnQllVrCLmCPa26mN5OuF3xGAwoPCuXsxa54oWYb-3B5CGaqE2Rt8NNanZ8Bf0_gEE2bKRa3fgdZpbWdfCTmZcM18egpuCgOYqY8WGBpzRjqJvotrGmSL3Ug0ED1PaMhlpAylYbX6sk5GJj2j22HlxWY83P7gXuLXdaJO8gAgzZt1cdAQ","RUNNER_TEMP_PROJECT_DIR":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp","npm_package_scripts_start":"nuxt start -p 3001","npm_config_ignore_scripts":"","FF_KUBERNETES_HONOR_ENTRYPOINT":"false","CI_PIPELINE_URL":"https://code.tls.nuskin.io/ns-am/accounts/app/signup/-/pipelines/441983","SONAR_IMAGE":"code.tls.nuskin.io:5005/custom-templates/custom-images:sonar-cached-RN-android-31-jdk17-node18","DEFAULT_SAST_EXCLUDED_PATHS":"spec, test, tests, tmp","S3_BUCKET_DEV":"nse-dev-nuskin-com-static","NSE_TLS_ACCOUNT_ID":"537237850156","DD_EXT_VER":"48","npm_package_resolutions__vue_component_compiler_utils":"^3.0.0","PATH":"/tmp/yarn--1744217154949-0.006208659737334088:/gitlab-runner-data/builds/ns-am/accounts/app/signup/node_modules/.bin:/usr/local/share/.config/yarn/link/node_modules/.bin:/usr/local/libexec/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/bin/node_modules/npm/bin/node-gyp-bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin","NODE":"/usr/local/bin/node","FF_EXPORT_HIGH_CARDINALITY_METRICS":"false","CI_JOB_STARTED_AT":"2025-04-09T16:45:03Z","CI_SERVER_VERSION_MINOR":"8","CI_RUNNER_DESCRIPTION":"nse-prd-us-west-2-runner-shared-i-043cffd563c65af06","SECRETS_ANALYZER_VERSION":"6","CI_RELEASE_DESCRIPTION":"# [1.96.0](https://code.tls.nuskin.io/ns-am/accounts/app/signup/compare/v1.95.0...v1.96.0) (2025-04-04)\n\n\n\n","npm_package_dependencies__nuskin_ns_checkout_common":"^3.34.42","npm_package_name":"signup-app","FF_USE_NEW_BASH_EVAL_STRATEGY":"false","FF_DISABLE_UMASK_FOR_KUBERNETES_EXECUTOR":"false","FF_MASK_ALL_DEFAULT_TOKENS":"true","HARBOR_HOST":"registry.shd.nuskin.io","CI_PROJECT_TITLE":"Signup","CI_PROJECT_VISIBILITY":"private","CI_COMMIT_TITLE":"Merge branch 'PA-39' into 'master'","GITLAB_USER_EMAIL":"jporter@nuskin.com","npm_package_resolutions_serialize_javascript":"3.1.0","npm_package_dependencies__nuskin_product_components":"^2.16.0","FF_USE_GIT_BUNDLE_URIS":"true","FF_USE_NATIVE_STEPS":"false","FF_USE_FLEETING_ACQUIRE_HEARTBEATS":"false","CI_SERVER":"yes","npm_package_resolutions_express":"4.17.3","npm_package_resolutions_parse_url":"8.1.0","FF_TIMESTAMPS":"false","CI_PROJECT_REPOSITORY_LANGUAGES":"java,javascript,vue,scss,shell","CI_PAGES_URL":"https://ns-am.pages.nuskin.io/accounts/app/signup","DS_EXCLUDED_PATHS":"__tests__/*,test/*","DOCKER_REGISTRY_DEPLOY_PASSWORD":"gldt-GgrCR7NqVCRP-3Y5qHDr","SEMP_BASIC_AUTH_DEVELOPMENT":"ZGV2ZWxvcG1lbnQtYWRtaW46ODU0czYyNXV1ams3aGE2MWxzNHViY2RjM2o=","npm_package_devDependencies_webpack":"^4.44.1","npm_package_devDependencies_msw":"^0.20.5","npm_package_devDependencies_eslint_plugin_json":"2.0.1","npm_package_devDependencies_eslint":"^5.16.0","npm_package_devDependencies__vue_test_utils":"^1.0.3","FF_SET_PERMISSIONS_BEFORE_CLEANUP":"true","FF_PRINT_POD_EVENTS":"false","HARBOR_URL":"https://registry.shd.nuskin.io","CI_SERVER_FQDN":"code.tls.nuskin.io","CI_COMMIT_AUTHOR":"Josh Porter <jporter@nuskin.com>","SONAR_URL":"https://sonar.nuskin.io","DS_EXCLUDED_ANALYZERS":"","HARBOR_REGISTRY":"registry.shd.nuskin.io","CS_TOKEN":"cs7c1b52de0a69f5da43cac376","CX_TENANT":"nuskin","npm_lifecycle_script":"cp ./env/prod.env ./.env && nuxt generate --fail-on-page-error","npm_package_dependencies_lodash_debounce":"^4.0.8","npm_package_scripts_build_test":"cp ./env/test.env ./.env && nuxt generate --fail-on-page-error","CI_JOB_IMAGE":"code.tls.nuskin.io:5005/custom-templates/custom-images:node16.18.1","CI_RUNNER_SHORT_TOKEN":"t1_B56G-","CI_PAGES_DOMAIN":"pages.nuskin.io","DEPLOY_SCRATCH_ON_EVERY_COMMIT":"true","npm_package_devDependencies_jest_localstorage_mock":"^2.4.3","npm_package_devDependencies__babel_plugin_proposal_nullish_coalescing_operator":"^7.10.4","npm_package_dependencies__nuskin_http_client":"^3.0.2","npm_package_scripts_test":"jest","npm_config_version_git_message":"v%s","CI_JOB_TIMEOUT":"3600","CI_REPOSITORY_URL":"https://gitlab-ci-token:glcbt-64_2rnmNS42y-PyvQxLde1p@code.tls.nuskin.io/ns-am/accounts/app/signup.git","CI_PROJECT_CLASSIFICATION_LABEL":"","CI_PIPELINE_NAME":"","SPLUNK_LAYER_ARN":"arn:aws:lambda:us-west-2:254067382080:layer:splunk-apm:108","GITLAB_ENV":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/gitlab_runner_env","npm_lifecycle_event":"build-prod","npm_package_resolutions_axios":"0.27.2","npm_package_devDependencies_jest_transform_stub":"^2.0.0","npm_package_devDependencies_jest_transform_css":"^2.0.0","npm_package_dependencies__nuxtjs_dotenv":"^1.4.1","npm_package_dependencies__nuskin_ns_product_lib":"^2.13.2","npm_package_dependencies__nuskin_ns_feature_flags_vue":"^2.0.2","npm_package_version":"1.0.0","FF_USE_POWERSHELL_PATH_RESOLVER":"false","FF_GIT_URLS_WITHOUT_TOKENS":"false","CI_ENVIRONMENT_TIER":"production","CI_API_GRAPHQL_URL":"https://code.tls.nuskin.io/api/graphql","CI_RUNNER_ID":"32565","S3_BUCKET_PROD":"nse-www-nuskin-com-static","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"build-prod\"],\"original\":[\"build-prod\"]}","npm_package_dependencies_vue_i18n":"^8.21.0","npm_package_dependencies_lodash_merge":"^4.6.2","npm_package_dependencies__nuskin_ns_market_config_vue":"2.0.0","npm_package_scripts_build":"nuxt build","npm_package_scripts_dev_mock":"cross-env APP_ENV=local MOCK_API=true nuxt --port 3001","npm_config_unsafe_perm":"true","CI_REGISTRY":"code.tls.nuskin.io:5005","CI_DEPENDENCY_PROXY_DIRECT_GROUP_IMAGE_PREFIX":"code.tls.nuskin.io:443/ns-am/accounts/app/dependency_proxy/containers","CI_API_V4_URL":"https://code.tls.nuskin.io/api/v4","MAVEN_IMAGE":"maven:3.6.3-amazoncorretto-8","GITLAB_USER_NAME":"Josh Porter","DEV_PORTAL_S3_BUCKET":"nuskin-dev-portalcontent-us-west-2-standard-dev","CX_BASE_URI":"https://ast.checkmarx.net","npm_package_devDependencies_portal_vue":"^2.1.7","CI_PIPELINE_IID":"3512","npm_package_dependencies__nuskin_ns_account":"5.11.1","npm_package_scripts_load_test":"echo \"Load testing AWS S3 is silly\"","npm_config_version_git_tag":"true","npm_config_version_git_sign":"","FF_USE_POD_ACTIVE_DEADLINE_SECONDS":"true","FF_USE_ADVANCED_POD_SPEC_CONFIGURATION":"false","CI_JOB_URL":"https://code.tls.nuskin.io/ns-am/accounts/app/signup/-/jobs/2183757","CI_SERVER_SHELL_SSH_HOST":"code.tls.nuskin.io","CI_RUNNER_EXECUTABLE_ARCH":"linux/amd64","npm_package_devDependencies_start_server_and_test":"^1.11.3","npm_package_dependencies_sass":"^1.26.10","npm_package_scripts_generate":"nuxt generate --fail-on-page-error","npm_config_strict_ssl":"true","FF_TEST_FEATURE":"false","FF_USE_INIT_WITH_DOCKER_EXECUTOR":"false","IS_CONTINUOUS_DEPLOYMENT":"true","SALESFORCE_DEPLOYMENT_APPROACH":"ORG_BASED","npm_package_resolutions_normalize_url":"4.5.1","npm_package_devDependencies_eslint_plugin_prettier":"^3.0.1","npm_package_scripts_lintfix":"eslint --ext .js,.vue components config layouts pages plugins test utils --fix","npm_package_scripts_test_watch":"jest --watch","CI_COMMIT_REF_SLUG":"v1-96-0","PROJECT_LANGUAGE":"JavaScript","CLIENT_APP_TYPE":"static","CI_DISPOSABLE_ENVIRONMENT":"true","PWD":"/gitlab-runner-data/builds/ns-am/accounts/app/signup","npm_execpath":"/opt/yarn-v1.22.19/bin/yarn.js","npm_package_dependencies__nuskin_ns_signup_api_client":"^2.12.1","FF_SECRET_RESOLVING_FAILS_IF_MISSING":"true","CI_RUNNER_TAGS":"[\"nse-prd\", \"us-west-2\", \"c5d.4xlarge\", \"gp3\", \"amd64\", \"linux\", \"shared-runner\", \"docker\"]","npm_package_resolutions_vue_loader":"15.9.3","npm_package_devDependencies_jest_css_modules":"^2.1.0","npm_package_devDependencies_flush_promises":"^1.0.2","npm_package_author_name":"mcassidy","CI_SERVER_TLS_CA_FILE":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/CI_SERVER_TLS_CA_FILE","CI_PIPELINE_ID":"441983","CI_PROJECT_PATH_SLUG":"ns-am-accounts-app-signup","CI_PROJECT_URL":"https://code.tls.nuskin.io/ns-am/accounts/app/signup","CI_CONFIG_PATH":".gitlab-ci.yml","CI_COMMIT_BEFORE_SHA":"0000000000000000000000000000000000000000","CI_ENVIRONMENT_SLUG":"prod","npm_package_devDependencies_postcss":"7","npm_package_devDependencies_eslint_plugin_vue":"^5.2.3","npm_package_devDependencies_cross_env":"^7.0.2","npm_package_devDependencies__babel_plugin_proposal_optional_chaining":"^7.11.0","npm_package_dependencies__nuskin_ns_shop":"4.1.1","npm_package_scripts_test_silent":"jest --reporters jest-silent-reporter --collectCoverage=false","npm_config_save_prefix":"^","npm_config_ignore_optional":"","CI_COMMIT_MESSAGE":"Merge branch 'PA-39' into 'master'\n\nUpdate: [NsAddress] updated api off of kong #PA-39\n\nCloses PA-39\n\nSee merge request ns-am/accounts/app/signup!583","NEXUS3_AUTH":"Z2l0bGFiOjNaNVdKTk1mek1hNg==","npm_package_resolutions_execa":"2.0.0","npm_package_resolutions_lodash":"4.17.21","npm_package_devDependencies_babel_eslint":"^10.1.0","npm_package_dependencies_vue2_transitions":"^0.3.0","npm_package_dependencies__nuxtjs_axios":"^5.12.1","FF_ENABLE_BASH_EXIT_CODE_CHECK":"false","CI_DEPENDENCY_PROXY_GROUP_IMAGE_PREFIX":"code.tls.nuskin.io:443/ns-am/dependency_proxy/containers","GITLAB_USER_ID":"111","SOLACE_BASIC_AUTH_DEVELOPMENT":"ZGV2ZWxvcG1lbnQtYWRtaW46bWJlb3IxaWo3OGcybGJham0xbm9wMHRkcW4=","SOLACE_API_TOKEN_PRODUCTION":"eyJhbGciOiJSUzI1NiIsImtpZCI6Im1hYXNfcHJvZF8yMDIwMDMyNiIsInR5cCI6IkpXVCJ9.eyJvcmciOiJudXNraW4iLCJvcmdUeXBlIjoiRU5URVJQUklTRSIsInN1YiI6Ijd3NHlmdXZtaG15IiwicGVybWlzc2lvbnMiOiJBQUFBQUlBUEFBQUFmemdBNEFFQUFBQUFBQUFBQUFBQUFJQ3h6b2NoSUFqZ0wvLy9nNVhmQmQ0RFdQTUQ2NThmIiwiYXBpVG9rZW5JZCI6InJ2aXRhZm44Z2hxIiwiaXNzIjoiU29sYWNlIENvcnBvcmF0aW9uIiwiaWF0IjoxNjgxODQ5MjY5fQ.iHKz7vUM_ABRU8G6JskfLABBpYyeaydVCGylq2OwwMb6gKpMzAqfxrs1lL2UZdip-lzQsoCXIFWQ-F1oMfPx41vUaZ3qS5w9wS7rfgfs8qqKp30ASiKL25CnBzxBeCNIC8hnE8MRF6hMg04PfmlU_lwbSv7Ohu0N0wbDaqE_I34vSfy37cKYpiuWmpKDAAYXi0S42xjkWY2zuvREa7GfFT3l8PpB_OSpeIMsfeKGjg97k9gXmWFCZ_tieMnJp6tblBmUe4SVwxj-GoeVDuprGBusqO-ALPfT0-phjtoBsOTgMf3E3aIApU4p4mepNcTIh6FUrCmM475n4sMxhNksEQ","NEXUS3_TOKEN":"a4c7394b-8d07-3634-8020-e9ac03ce123f","npm_package_dependencies__nuskin_configuration_sdk":"2.2.5","INIT_CWD":"/gitlab-runner-data/builds/ns-am/accounts/app/signup","CI_JOB_STATUS":"running","CI_JOB_NAME":"deploy-to-prod","CI_SERVER_SHELL_SSH_PORT":"22","SAST_EXCLUDED_PATHS":"spec, test, tests, tmp","SOLACE_API_TOKEN_TESTING":"eyJhbGciOiJSUzI1NiIsImtpZCI6Im1hYXNfcHJvZF8yMDIwMDMyNiIsInR5cCI6IkpXVCJ9.eyJvcmciOiJudXNraW4iLCJvcmdUeXBlIjoiRU5URVJQUklTRSIsInN1YiI6Ijd3NHlmdXZtaG15IiwicGVybWlzc2lvbnMiOiJBQUFBQUlBUEFBQUFmemdBNEFFQUFBQUFBQUFBQUFBQUFJQ3h6b2NoSUFqZ0wvLy9nNVhmQmQ0RFdQTUQ2NThmIiwiYXBpVG9rZW5JZCI6Im1sNm16djBibGp2IiwiaXNzIjoiU29sYWNlIENvcnBvcmF0aW9uIiwiaWF0IjoxNjcwNjA2MzA5fQ.D96MqCfp9DobIKyLaDMFFB8A3wFcPNu4uJBWn4o3PBdS-EdFj-VmlutdgPMScTmdn0tcvX7NGRwvaZs5MJy2-rxuJmDepecEQ6MhLG9ViZIHESbstI6-11nnD9BC2YJ-67ymujVpebN-XE_VbBHSeQnGXsXwjeHk0ZuR_KyNqgSiGrhvM7Q9BLXPo3zULjxoSwclllSFg75EduxRDd-o1PpN2AtyloARrct8z7zWwe3VCPFMnsHHH_2WR3skcCLp10ryLpPLMQQHPt6A6bNxZrpU6i_1pooOIz1MfsGWZXn7V3tCEyB--KpwZUG5dwrJE8slcE2GdbnFq6fWAksaVQ","npm_package_resolutions_sass":">=1.23.0 <1.26.0","npm_package_devDependencies_eslint_config_google":"^0.14.0","npm_package_devDependencies__peculiar_webcrypto":"^1.4.3","npm_package_dependencies__nuskin_ns_gtm":"^1.0.0","NODE_ENV":"production","_applied":"true"}
    }
    // Only set once
    if (!process.static && context.req) {
      app.context.req = context.req
    }
    if (!process.static && context.res) {
      app.context.res = context.res
    }
    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = formatUrl(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  let path = decodeURI(window.location.pathname)
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }
  // To get matched with sanitized router.base add trailing slash
  if (base && (path.endsWith('/') ? path : path + '/').startsWith(base)) {
    path = path.slice(base.length)
  }
  return (path || '/') + window.location.search + window.location.hash
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

/**
 * Format given url, append query to url query string
 *
 * @param  {string} url
 * @param  {string} query
 * @return {string}
 */
function formatUrl (url, query) {
  let protocol
  const index = url.indexOf('://')
  if (index !== -1) {
    protocol = url.substring(0, index)
    url = url.substring(index + 3)
  } else if (url.startsWith('//')) {
    url = url.substring(2)
  }

  let parts = url.split('/')
  let result = (protocol ? protocol + '://' : '//') + parts.shift()

  let path = parts.join('/')
  if (path === '' && parts.length === 1) {
    result += '/'
  }

  let hash
  parts = path.split('#')
  if (parts.length === 2) {
    [path, hash] = parts
  }

  result += path ? '/' + path : ''

  if (query && JSON.stringify(query) !== '{}') {
    result += (url.split('?').length === 2 ? '&' : '?') + formatQuery(query)
  }
  result += hash ? '#' + hash : ''

  return result
}

/**
 * Transform data object to query string
 *
 * @param  {object} query
 * @return {string}
 */
function formatQuery (query) {
  return Object.keys(query).sort().map((key) => {
    const val = query[key]
    if (val == null) {
      return ''
    }
    if (Array.isArray(val)) {
      return val.slice().map(val2 => [key, '=', val2].join('')).join('&')
    }
    return key + '=' + val
  }).filter(Boolean).join('&')
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export function urlJoin () {
  return [].slice
    .call(arguments)
    .join('/')
    .replace(/\/+/g, '/')
    .replace(':/', '://')
}

export function stripTrailingSlash (path) {
  return path.replace(/\/+$/, '') || '/'
}

export function isSamePath (p1, p2) {
  return stripTrailingSlash(p1) === stripTrailingSlash(p2)
}
